import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { CITY_ZOOM, DEFAULT_ZOOM, MAP_CENTER, STREET_ZOOM } from "../../constants/map";
import { SzTab, SzTypographie } from "@suezenv/react-theme-components";
import SzLoadSpinner from "../utils";
import {
    IPagination,
    ISignalStatus,
    SignalGeoJSONProperties,
    SignalResponseFormat,
    SignalType,
    IStatusStats,
    ISort,
    ISignalAdvancedFavoriteFilters,
} from "../../type/signal.type";
import { useTranslationWithPrefix } from "../../hooks/translation";
import SignalContext from "../../context/signal.context";
import SignalMap from "./map/signal-map";
import SignalList from "./list/signal-list";
import "./signal-view.scss";
import SignalFilterForm from "./filter-form/signal-filter.form";
import { SignalRequest } from "../../type/signal.type";
import requestTypeReferenceRepository from "../../services/repository/request-type-reference.repository";
import { RequestTypeReference, WorkflowStatusReference } from "../../type/organization.type";
import { ISignalFilter } from "../../type/signal.type";
import { useContract, useOrganization } from "../../hooks/organization";
import workflowStatusReferenceRepository from "../../services/repository/status-reference.repository";
import signalService from "../../services/api/signal-service";
import { OptionItem } from "../../form/SzMultipleCheckbox/SzMultipleCheckbox";
import { UserType } from "../../type/user.type";
import { useIsHasSignalService } from "../../hooks/service";
import Unauthorized from "../message/unauthorized";
import { DEFAULT_END_DATE, DEFAULT_START_DATE, DEFAULT_STATUS_FILTER, PAGINATION_NUMBER_PER_PAGE, SORT_FIELD_DEFAULT, SORT_ORDER_DEFAULT } from "../../constants/filters";
import { AxiosResponse } from "axios";
import i18n from "../../../i18n";
import { I18nextProvider } from "react-i18next";
import MapViewChanger from "../signal-create/map-view-changer/map-view-changer";
import { GeographicalLatLng, SearchAddressFilters } from "../../type/Address.type";
import ContractFilterService from "../../services/api/contract-filter-service";
import { getLocalStorage, getSessionStorage, setLocalStorage, setSessionStorage } from "../../helpers/storage.util";
import { LIST_DEFAULT_PAGE } from "../../constants/list";
import { DEFAULT_RADIUS } from "../../constants/filters";
import mercureService from "../../services/mercure/mercure-service";
import _ from 'lodash';
import { config } from "../../config";
import { downloadFile } from "../../helpers/file.util";
import { dateToString } from "../../helpers/date.util";
import { FilterQueryParams } from "../../type/filter.type";
import useSignalCategory from "../../hooks/signal-category";

interface ISignalView {
    ref: any
    geoServer?: {
        url: string;
        workspace: string;
        layer: string;
        authkey: string;
    };
    user: UserType;
    addSignal: () => void;
    goSignalDetail: (signalId: string) => void;
    signalType: SignalType;
    geographicalPriority?: GeographicalLatLng
    enableBatchExport?: boolean
    geoShape?: GeoJSON.Feature;
    geoShapeCoordinatesRestriction?: []; // this would be a GeoJSON.Position, but the nesting level is variable (e.g., Polygon, MultiPolygon)
    setGeoShapeCoordinatesRestriction?: React.Dispatch<React.SetStateAction<[]|null>>,
    favoriteFiltersList?: ISignalAdvancedFavoriteFilters[];
    setFavoriteFiltersList?: (favoriteFiltersList: ISignalAdvancedFavoriteFilters[]) => void;
    // this function leverages the "export signals" feature by acting as an interceptor
    // allowing the Widget parent component to modify or reformat the data before it is exported.
    prepareDataForDownloadExport?: (data: any) => any;
}

export const SignalView: React.FC<ISignalView> = forwardRef(({
    geoServer,
    user,
    addSignal,
    goSignalDetail,
    signalType,
    enableBatchExport,
    geographicalPriority,
    geoShape,
    geoShapeCoordinatesRestriction,
    setGeoShapeCoordinatesRestriction,
    favoriteFiltersList,
    setFavoriteFiltersList,
    prepareDataForDownloadExport,
}, ref) => {
    const TAB_MAP = 'tab-map'
    const TAB_LIST = 'tab-list'
    const LIST_TAB_KEY = 'listTab'
    const SIGNAL_TYPE_MAP = 'map'
    const SIGNAL_TYPE_LIST = 'list'
    const SESSION_FILTERS_KEY = 'appliedFilters'
    const VISIBILITY_STATE ="visible"

    const [signalList, setSignalList] = useState<{
        geojsonData?: GeoJSON.FeatureCollection<GeoJSON.Point, SignalGeoJSONProperties>;
        signals?: SignalRequest[];
        pagination: IPagination;
        sort: ISort;
    }>();
    const getSessionFilters = () => getSessionStorage(SESSION_FILTERS_KEY)
    const setSessionFilters = (appliedFilter: ISignalFilter) => setSessionStorage(SESSION_FILTERS_KEY, JSON.stringify(appliedFilter))

    const getStorageTab = () => getLocalStorage(LIST_TAB_KEY)
    const setStorageTab = (tabName: string) => setLocalStorage(LIST_TAB_KEY, tabName)

    /**
     * Resets geographical shapes to prevent retaining map positions.
     */
    const resetGeoShape = (): void => {
        if (setGeoShapeCoordinatesRestriction) {
            setGeoShapeCoordinatesRestriction([]);
        }
    };

    /**
     * Computes the geographical shapes based on the optional coordinates restrictions.
     */
    const getGeoShape = (): GeoJSON.Feature => {
        if (_.isEmpty(geoShapeCoordinatesRestriction)) {
            return geoShape as GeoJSON.Feature;
        }

        return {
            ...geoShape,
            geometry: {
                ...geoShape?.geometry,
                coordinates: geoShapeCoordinatesRestriction,
            }
        } as GeoJSON.Feature;
    };

    const getDefaultSignalType = (): SignalType => {
        if (signalType) {
            return signalType;
        }

        const storedTab = getStorageTab();

        if (storedTab === TAB_LIST) {
            return SIGNAL_TYPE_LIST as SignalType;
        }

        return SIGNAL_TYPE_LIST as SignalType;
    };

    const getDefaultSignalTab = (): string => {
        if (signalType === SIGNAL_TYPE_LIST) {
            return TAB_LIST;
        }

        if (signalType === SIGNAL_TYPE_MAP) {
            return TAB_MAP;
        }

        const storedTab = getStorageTab();

        if (storedTab) {
            return storedTab;
        }

        return TAB_LIST;
    };

    const initFilters = (): ISignalFilter => {
        const lastFilters = getSessionFilters()
        if (lastFilters) {
            return JSON.parse(lastFilters)
        }
        return {
            status: DEFAULT_STATUS_FILTER,
            startDate: DEFAULT_START_DATE,
            endDate: DEFAULT_END_DATE,
        }
    }



    const [appliedFilter, setAppliedFilter] = useState<ISignalFilter>(initFilters());
    const [queryParamsAppliedFilter, setQueryParamsAppliedFilter] = useState<FilterQueryParams|null>(null);
    const [requestTypes, setRequestTypes] = useState<RequestTypeReference[]>([]);
    const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatusReference[]>([]);
    const [statusStats, setStatusStats] = useState<ISignalStatus>();
    const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
    const [signalTypeChange, setSignalTypeChange] = useState<string>(getDefaultSignalType())
    const [signalTab, setSignalTab] = useState<string>(getDefaultSignalTab())
    const organization = useOrganization(user);
    const isHasSignalService = useIsHasSignalService(user);
    const [loader, setLoader] = useState(true);
    const trans = useTranslationWithPrefix("signalement", "view_signal");
    const contract = useContract(user)
    const getSignalCategory = useSignalCategory(requestTypes);
    const [searchAddressFilters, setSearchAddressFilters] = useState<SearchAddressFilters>()
    const [currentPage, setCurrentPage] = useState<number>(LIST_DEFAULT_PAGE);
    const [reload, setReload] = useState<boolean>(false);
    const mercureTopicbuilder = "realtime-event/VILLAGILE-SIGNAL/contract"

    const selectSignalTabHandle = (tabName: string) => {
        resetGeoShape();
        setSignalTab(tabName)
        setSignalTypeChange(tabName === TAB_LIST ? SIGNAL_TYPE_LIST : SIGNAL_TYPE_MAP);
        // session
        setStorageTab(tabName)
    };

    const EnableShowFilterLoader = (filters: ISignalFilter) => {
        if (Object.keys(filters).includes("instigators")) {
            setLoader(true);
        }
    }

    const getSignals = (currentPage?: number) => {
        setLoader(true);
        applyFilter(appliedFilter, currentPage);
    };

    const applyFilter = (filters: ISignalFilter, currentPage?: number) => {
        const queryParams: any = {
            pagination: {
                size: PAGINATION_NUMBER_PER_PAGE,
            },
            sort: {
                fields: [SORT_FIELD_DEFAULT],
                order: [SORT_ORDER_DEFAULT],
            },
        };

        if (filters.address) {
            if ('municipality' === filters.address.type) {
                queryParams.filterFields = [`address.city__EQ__[${filters.address.city}]`];
                setZoom(CITY_ZOOM);
            } else if (filters.address.latitude && filters.address.longitude) {
                queryParams.distance = {
                    length: filters.radius ?? DEFAULT_RADIUS,
                    latitude: filters.address.latitude,
                    longitude: filters.address.longitude,
                };
                setZoom(STREET_ZOOM);
            }
        }

        if(filters.q) {
            queryParams.q = filters.q;
        }

        if (currentPage) {
            queryParams["pagination"]["page"] = currentPage;
        }
        let format: SignalResponseFormat = "json";

        if (signalTypeChange === SIGNAL_TYPE_MAP) {
            format = "geojson";
            queryParams["exportFields"] = [
                "id",
                "currentStatus.status.id",
                "currentStatus.status.label",
                "address.address",
                "applicationUser.fullName",
                "requestType",
            ];
            queryParams["exportLabels"] = [
                "id",
                "currentStatusId",
                "currentStatusLabel",
                "fullAddress",
                "fullName",
                "requestType",
            ];
        }
        // display Loader when Filter submit button is pressed
        EnableShowFilterLoader(filters);
        queryParams.filter = parseFilter(filters);
        setAppliedFilter(filters);
        setQueryParamsAppliedFilter(queryParams);
        signalService.getAll(queryParams, format).then(({ data, headers }) => {
            setSignalList({
                geojsonData:
                    signalTypeChange === SIGNAL_TYPE_MAP
                        ? (data as GeoJSON.FeatureCollection<GeoJSON.Point, SignalGeoJSONProperties>)
                        : undefined,
                signals: signalTypeChange === SIGNAL_TYPE_LIST ? (data as SignalRequest[]) : undefined,
                pagination: {
                    totalCount: headers["x-pagination-total-count"],
                    pageCount: headers["x-pagination-page-count"],
                    currentPage: headers["x-pagination-current-page"],
                    perPage: headers["x-pagination-per-page"],
                },
                sort: {
                    field: SORT_FIELD_DEFAULT,
                    order: SORT_ORDER_DEFAULT,
                }
            });

            if (data) {
                setLoader(false)
            }
        })
    };

    const parseFilter = (filters: ISignalFilter) => {
        const filter: any = {
            createdAt: {},
        };
        if (filters.requestTypes) {
            filter.requestType = filters.requestTypes.map((requestType: OptionItem) => requestType.value);
        }
        if (filters.status) {
            filter.status = filters.status.map((status: OptionItem) => status.value);
        }
        if (filters.startDate) {
            filter.createdAt.start = filters.startDate;
        }
        if (filters.endDate) {
            filter.createdAt.end = filters.endDate + 'T23:59:59Z';
        }
        
        return filter;
    };

    const downloadExport = (): void => {
        const queryParamsExport: FilterQueryParams = { ...queryParamsAppliedFilter };

        try {
            const exportParameters = _.attempt(JSON.parse, config.apiExportParameters);

            if (!_.isError(exportParameters) && !_.isEmpty(exportParameters)) {
                queryParamsExport.exportLabels = _.map(exportParameters, 'label');
                queryParamsExport.exportFields = _.map(exportParameters, 'field');
            }
        } catch {
        }

        signalService.getAll(queryParamsExport, 'csv').then(({ data }) => {
            if (_.isFunction(prepareDataForDownloadExport)) {
                data = prepareDataForDownloadExport(data);
            }

            downloadFile(data || '', `export_signalements_${dateToString(new Date(), 'YYYY-MM-DD_HHmmss')}.csv`, 'text/csv');
        });
    };

    const getStatus = () => {
        const queryParams: any = {};
        if (appliedFilter.address) {
            if ('municipality' === appliedFilter.address.type) {
                queryParams.filterFields = [`address.city__EQ__[${appliedFilter.address.city}]`];
            } else if (appliedFilter.address.latitude && appliedFilter.address.longitude) {
                queryParams.distance = {
                    length: appliedFilter.radius ?? DEFAULT_RADIUS,
                    latitude: appliedFilter.address.latitude,
                    longitude: appliedFilter.address.longitude,
                };
            }
        }

        if (appliedFilter.q) {
            queryParams.q = appliedFilter.q;
        }
        queryParams.filter = parseFilter(appliedFilter);

        signalService.getStatusStats(queryParams).then((response: AxiosResponse<IStatusStats>) => {
            setStatusStats(response.data?.cumulated);
        })
    }

    useEffect(() => {
        if(reload){
            getSignals(currentPage);
            getStatus();
            setReload(false);
        }
    }, [reload]);

    // Fonction pour éviter de rafraichir plusieurs fois si plusieur messages sont reçu rapidement
    // const debounceReload = useCallback(() => {
    //     if (debounceTimerRef.current) {
    //         clearTimeout(debounceTimerRef.current);
    //     }
    //
    //     debounceTimerRef.current = setTimeout(() => {
    //         setReload(true);
    //         debounceTimerRef.current = null;
    //     }, 1000);
    // }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === VISIBILITY_STATE) {
                setReload(true);
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return (): void => {
            resetGeoShape();
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        let subscriptionKey: string | null = null;

        if (!contract) return;

        const topics = [`${mercureTopicbuilder}/${contract.id}`];

        const handleMessage = (data: any) => {
            if (document.visibilityState === VISIBILITY_STATE) {
                setReload(true);
            }
        };

        mercureService.subscribe(topics, handleMessage, {
            autoReconnect: true,
            debounceMs: 1000,
            onError: (err) => console.error("[Mercure] Error:", err),
        }).then((key) => {
            subscriptionKey = key;
        }).catch((error) => {
            console.error("[Mercure] Subscription error:", error);
        });

        return () => {
            if (subscriptionKey) {
                mercureService.unsubscribe(subscriptionKey, handleMessage);
            }
        };
    }, [contract, mercureTopicbuilder]);

    useEffect(() => {
        if (signalList?.pagination?.pageCount !== undefined &&
            currentPage > signalList?.pagination?.pageCount)
            {
                getSignals(signalList?.pagination?.pageCount);
                setCurrentPage(signalList?.pagination?.pageCount || LIST_DEFAULT_PAGE);
            }
    }, [signalList]);

    useEffect(() => {
        setSessionFilters(appliedFilter)
    }, [appliedFilter]);

    useEffect(() => {
        let isMounted = true;
        if (organization) {
            requestTypeReferenceRepository.getAll(organization.id).then(({ data }) => {
                if (isMounted) {
                    setRequestTypes(data);
                    const requestTypesToFilter = data.map((state: RequestTypeReference) => {
                        return { label: state.label, value: state.requestType };
                    });
                    if (!(appliedFilter.requestTypes && appliedFilter.requestTypes.length > 0)) {
                        setAppliedFilter({ ...appliedFilter, requestTypes: requestTypesToFilter })
                    }
                }
            });
            workflowStatusReferenceRepository.getAll(organization.id).then(({ data }) => {
                if (isMounted) {
                    setWorkflowStatus(data);
                }
            });
        }

        // Clean-up:
        return () => {
            isMounted = false;
        }

    }, [organization]);

    useEffect(() => {
        if (organization) {
            getSignals(currentPage);
            if(signalTypeChange === SIGNAL_TYPE_LIST) {
                getStatus();
            }
        }
    }, [organization, appliedFilter, signalTypeChange]);


    useEffect(() => {
        if (contract?.id) {
            ContractFilterService.getAddressFilters(contract.id)
                .then((response: any) => {
                    setSearchAddressFilters(response?.data ? response.data : undefined)
                })
        }
    }, [contract]);

    /**
     * Exposes internal methods of this component to the parent via ref.
     */
    useImperativeHandle(ref, () => ({
        getSignalCategory,
    }));

    return (
        <I18nextProvider i18n={i18n}>
            {isHasSignalService ? (
                <SignalContext.Provider
                    value={{
                        geoServer,
                        goSignalDetail,
                        enableBatchExport,
                    }}
                >
                    <div className='row-fluid spinner-container'>
                        <SzLoadSpinner loader={loader} variant='info' />
                    </div>
                    <div className="signal-view">
                        <div className="signal-view-container">
                            <div className='mr-3 ml-3 mt-3 mb-0 p-2 pl-0 signal-header border-left'>
                                <div className="row-fluid">
                                    <div className='col-5'>
                                        <SzTypographie variant="h1" className='m-0 request-primary-text'>
                                            {trans('signal_header_title')}
                                        </SzTypographie>
                                        <SzTypographie className='m-0' color="inactive">
                                            {trans('signal_header_sub_title')}
                                        </SzTypographie>
                                    </div>
                                </div>
                            </div>
                            <SzTab
                                activeKey={signalTab}
                                id="signal-tab"
                                selectTabHandle={selectSignalTabHandle}
                            >
                                <SzTab.SzTabContent eventKey={TAB_MAP} title={trans('tab_map_title')}>
                                    <SignalFilterForm
                                        requestTypes={requestTypes}
                                        status={workflowStatus}
                                        appliedFilters={appliedFilter}
                                        onSubmit={applyFilter}
                                        geographicalPriority={geographicalPriority}
                                        searchAddressFilters={searchAddressFilters}
                                        addSignal={addSignal}
                                        favoriteFiltersList={favoriteFiltersList ?? []}
                                        setFavoriteFiltersList={setFavoriteFiltersList ?? (() => {})}
                                        downloadExport={downloadExport}
                                    />
                                    <div className="container-fluid">
                                        <div className="row-fluid">   
                                            <div className='col-12 p-2 signal-map'>
                                                {signalTypeChange === SIGNAL_TYPE_MAP && signalList?.geojsonData && (
                                                    <SignalMap
                                                        requestTypes={requestTypes}
                                                        defaultZoom={zoom}
                                                        center={MAP_CENTER}
                                                        geojsonData={signalList?.geojsonData}
                                                        geoShape={getGeoShape()}
                                                    >
                                                        {appliedFilter?.address?.address && <MapViewChanger zoom={zoom} center={[appliedFilter?.address?.latitude, appliedFilter?.address?.longitude]} />}
                                                    </SignalMap>

                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </SzTab.SzTabContent>

                                <SzTab.SzTabContent eventKey={TAB_LIST} title={trans('tab_list_title')}>
                                    <SignalFilterForm
                                        requestTypes={requestTypes}
                                        status={workflowStatus}
                                        appliedFilters={appliedFilter}
                                        onSubmit={applyFilter}
                                        geographicalPriority={geographicalPriority}
                                        searchAddressFilters={searchAddressFilters}
                                        addSignal={addSignal}   
                                        favoriteFiltersList={favoriteFiltersList ?? []}
                                        setFavoriteFiltersList={setFavoriteFiltersList ?? (() => {})}
                                        downloadExport={downloadExport}
                                    />
                                    <div className='container-fluid'>
                                        <div className="row-fluid">
                                            {signalTypeChange === SIGNAL_TYPE_LIST && signalList?.signals && (
                                                <SignalList
                                                    signals={signalList.signals}
                                                    catergories={requestTypes}
                                                    statusStats={statusStats}
                                                    getSignals={(currentPage: number) => getSignals(currentPage)}
                                                    user={user}
                                                    workflowStatus={workflowStatus}
                                                    pagination={signalList?.pagination}
                                                    sort={signalList?.sort}
                                                    currentPage={currentPage}
                                                    setCurrentPage={setCurrentPage}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </SzTab.SzTabContent>
                            </SzTab>
                        </div>
                    </div>
                </SignalContext.Provider>
            ) : (
                <Unauthorized />
            )}
        </I18nextProvider>
    );
});
